import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, Grid2X2, LogOut, Building2, Plus, ShieldCheck, Globe, User as UserIcon, Languages } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/lib/i18n';
import { PillotLogo } from '@/components/ModuleIcon';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export const LanguageToggle = ({ className = '' }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`inline-flex items-center rounded-full border border-border bg-white p-0.5 text-xs font-medium ${className}`} data-testid="language-toggle">
      {['id', 'en'].map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => setLang(l)}
          data-testid={`language-toggle-${l}`}
          className={`rounded-full px-2.5 py-1 uppercase transition-colors ${lang === l ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const SubscriptionBadge = ({ subscription, t }) => {
  if (!subscription) return null;
  const s = subscription.effective_status;
  const map = {
    trial: 'bg-amber-50 text-amber-800 border-amber-200',
    active: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    past_due: 'bg-rose-50 text-rose-800 border-rose-200',
    expired: 'bg-rose-50 text-rose-800 border-rose-200',
    cancelled: 'bg-slate-100 text-slate-700 border-slate-200',
  };
  return (
    <Link to="/settings/billing" data-testid="subscription-badge">
      <Badge variant="outline" className={`rounded-full font-medium ${map[s] || ''}`}>
        {t(s)}{subscription.days_left != null && (s === 'trial' || s === 'active') ? ` · ${subscription.days_left} ${t('days_left')}` : ''}
      </Badge>
    </Link>
  );
};

export const AppShell = ({ children, moduleName, moduleIcon, actions, fullBleed = false, className = '' }) => {
  const { user, companies, company, logout, switchCompany } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const initials = (user?.full_name || user?.email || '?').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className={`${fullBleed ? 'h-screen overflow-hidden' : 'min-h-screen'} flex flex-col ${className}`}>
      <header className="sticky top-0 z-40 border-b border-border bg-[rgba(255,255,255,0.88)] backdrop-blur">
        <div className="flex h-14 items-center gap-3 px-3 sm:px-5">
          <Link to="/apps" className="flex shrink-0 items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-muted transition-colors" data-testid="nav-apps-home">
            <Grid2X2 size={18} className="text-primary" />
            <PillotLogo className="h-6" />
          </Link>
          {moduleName && (
            <div className="hidden sm:flex items-center gap-2 pl-3 border-l border-border">
              {moduleIcon}
              <span className="font-display font-semibold text-sm" data-testid="module-name">{moduleName}</span>
            </div>
          )}
          <div className="flex-1 min-w-0 flex items-center gap-2 justify-end">
            {actions}
            {company && <span className={actions ? 'hidden xl:inline-flex' : ''}><SubscriptionBadge subscription={company.subscription} t={t} /></span>}
            <LanguageToggle className="hidden sm:inline-flex" />
            {company && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="rounded-xl gap-1.5 shrink-0 max-w-[180px] px-2 sm:px-3" data-testid="company-switcher">
                    <Building2 size={16} className="text-primary shrink-0" />
                    <span className="hidden sm:inline truncate text-sm font-medium">{company.name}</span>
                    <ChevronDown size={14} className="shrink-0 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel>{t('switch_company')}</DropdownMenuLabel>
                  {companies.map((c) => (
                    <DropdownMenuItem key={c.id} onClick={() => switchCompany(c.id).then(() => navigate('/apps'))} data-testid={`company-switch-${c.slug}`} className="flex justify-between">
                      <span className="truncate">{c.name}{c.role === 'super_admin' && <span className="ml-1 text-[10px] text-muted-foreground">· super admin</span>}</span>
                      {c.id === company.id && <span className="text-xs text-[hsl(var(--pillot-mint))]">●</span>}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/onboarding?new=1')} data-testid="company-switcher-new">
                    <Plus size={14} className="mr-2" /> {t('new_company')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="rounded-full ring-offset-2 focus-visible:ring-2 focus-visible:ring-ring" data-testid="user-menu">
                  <Avatar className="h-8 w-8 border border-border">
                    {user?.avatar_url && <AvatarImage src={user.avatar_url} alt="" />}
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">{initials}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuLabel className="font-normal">
                  <div className="text-sm font-semibold truncate">{user?.full_name}</div>
                  <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5 sm:hidden flex items-center justify-between text-sm"><span className="flex items-center gap-2"><Languages size={14} />{t('language')}</span><LanguageToggle /></div>
                <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="user-menu-settings"><UserIcon size={14} className="mr-2" />{t('settings')}</DropdownMenuItem>
                {user?.is_super_admin && (
                  <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="user-menu-admin"><ShieldCheck size={14} className="mr-2" />{t('super_admin')}</DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => logout().then(() => navigate('/login'))} data-testid="user-menu-logout" className="text-destructive focus:text-destructive">
                  <LogOut size={14} className="mr-2" />{t('logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      <main className={`min-w-0 overflow-x-clip ${fullBleed ? 'flex-1 flex flex-col min-h-0' : 'flex-1'}`} key={location.pathname}>{children}</main>
    </div>
  );
};

export const PageHeader = ({ title, subtitle, actions, icon }) => (
  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6 fade-up">
    <div className="flex items-start gap-3">
      {icon}
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm md:text-base text-muted-foreground mt-1">{subtitle}</p>}
      </div>
    </div>
    {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
  </div>
);

export const EmptyState = ({ icon: Icon = Globe, title, description, action }) => (
  <div className="rounded-2xl border border-dashed border-border bg-white p-10 text-center shadow-soft" data-testid="empty-state">
    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary"><Icon className="text-primary" size={26} /></div>
    <h3 className="font-display text-lg font-semibold">{title}</h3>
    {description && <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">{description}</p>}
    {action && <div className="mt-5 flex justify-center">{action}</div>}
  </div>
);
